import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Row, Select, Space, Table, Typography, message, Modal as AntModal, Spin } from "antd";
import type { TableProps } from "antd";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { profileStore } from "src/stores/profile-store";
import { utilsStore } from "src/stores/utils-store";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";

import { excelStore } from "src/stores/excel-store.interface";
import { MonthsDisplayNoTotal, UnitAllEnum } from "src/entity/common/unit.constant";
import { TbRefresh } from "react-icons/tb";
import ModalApproveMonth from "src/components/ModalApproveMonth/ModalApproveMonth";
import { componentPalette } from "src/theme/component-palette";
import { BsTable } from "react-icons/bs";
import { HiOutlineClipboardDocumentCheck } from "react-icons/hi2";
import { PiChartBarFill } from "react-icons/pi";
import { RolesNameEnum } from "src/entity/common/roles.enum";
import ModalApproveStep from "src/components/ModalApproveStep/ModalApproveStep";
import { useSearchParams } from "react-router-dom";
import { ApproveBody, ResponseAPI } from "src/entity/voc-approve/voc-approve.entity";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getFilterWithEmptyMasterdata } from "src/utils/utils-object";
import {
  ObjectSummaryWithUnit,
  ReportMonthData,
  ReportTotalMonthData,
  ReportUnitValue,
  TmpReportEmissionData,
  VocEmissionReport,
} from "src/entity/others/report-voc-emission.entity";
import { formatNumber, parseAndFormatScientificNumber } from "src/utils/process-number";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { optionsSelectYears } from "src/utils/array";
import { othersApproveStore } from "src/stores/others-approve-store";
import { othersStore } from "src/stores/others-store";
import { FilterOthersReportVocsEmission, ResponseOthersReportVocs } from "src/entity/others/fill-emission";
import { isNull, omitBy, uniqueId } from "lodash";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import { canApproveAndViewMainReport, canApproveAndViewTable } from "../../PermissionCheck/PermissionCheck";

const { Text } = Typography;
interface ReportVocEmissionTableProps {}

const ReportVocEmissionTable: React.FC<ReportVocEmissionTableProps> = ({}) => {
  const menu = "others";
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();

  const [searchParams, setSearchParams] = useSearchParams();
  const approveId = searchParams.get("approveId");
  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");
  const approveNoParam = searchParams.get("approveNo");

  const prevCompany: any = useRef();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [selectedUnit, setSelectedUnit] = useState<UnitAllEnum>(UnitAllEnum.KG);
  const [openModalApproveMonth, setOpenModalApproveMonth] = useState<boolean>(false);
  const [selectedButton, setSelectedButton] = useState<string>("table");
  const [openModalApproveStep, setOpenModalApproveStep] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState(0);
  const [selectedEmission, setSelectedEmission] = useState<string>("ALL");
  const { permissions } = useNavigationMenu();

  // 1. Profile zone !
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const userId = profile?.userId;
  const email = profile?.email;
  const name = profile?.name;

  // 2. Master data zone
  const setLoadingAll = othersStore(useShallow((state) => state.setLoadingAll));
  const loadingAll = othersStore(useShallow((state) => state.loading.loadingAll));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));
  const getMasterDataEmission = utilsStore(useShallow((state) => state.getMasterDataEmission));
  const masterDataEmission = utilsStore(useShallow((state) => state.masterDataEmission));

  // 3. Report Voc Zone
  const searchReportVocEmission = othersStore(useShallow((state) => state.getAllReportVocEmission));
  const dataReportVoc = othersStore(useShallow((state) => state.dataReportVoc));
  const getFilterColumnReportVocEmission = othersStore(useShallow((state) => state.getAllDistinctColFillEmission));
  const filterReportVocEmission = othersStore(useShallow((state) => state.dataFilterColFillEmission));
  // const calculateReportAfterRejectApproval = reportWasteWaterStore(useShallow((state) => state.calculateReportVocAfterRejectApproval));

  const setSelectedYearExcel = excelStore(useShallow((state) => state.setSelectedYear));
  const queryStr = excelStore(useShallow((state) => state.queryStr));
  const setQueryStringExcel = excelStore(useShallow((state) => state.setQueryStr));

  const getMonthCanApprove = othersApproveStore(useShallow((state) => state.getMonthCanApprove));
  const loadingOptionMonth = othersApproveStore(useShallow((state) => state.loading));
  const optionMonth = othersApproveStore(useShallow((state) => state.optionMonth));
  const createSubmitReportVoc = othersApproveStore(useShallow((state) => state.createSubmitReportVoc));
  const loadingSubmitVocReport = othersApproveStore(useShallow((state) => state.loadingSubmitVocReport));
  const getInfoStepApprove = othersApproveStore(useShallow((state) => state.getInfoStepApprove));
  const loadingGetApprove = othersApproveStore(useShallow((state) => state.loadingGetApprove));
  const approveData = othersApproveStore(useShallow((state) => state.approveData));
  const selectedApprove = othersApproveStore(useShallow((state) => state.selectedApprove));
  const getOptionApprove = othersApproveStore(useShallow((state) => state.getOptionApprove));
  const optionApprove = othersApproveStore(useShallow((state) => state.optionApprove));
  const updateApproveStep = othersApproveStore(useShallow((state) => state.updateApproveStep));

  // For new ui approval page
  const [selectedYearApprove, setSelectedYearApprove] = useState<number | null>();
  const [selectedMonthApprove, setSelectedMonthApprove] = useState<string | null>();
  const [selectedNoApprove, setSelectedNoApprove] = useState<number | null>();

  const fetchInfoStepApprove = async () => {
    if (selectedYearApprove && selectedMonthApprove && selectedNoApprove) {
      const query = new URLSearchParams({
        menu,
        year: selectedYearApprove.toString(),
        month: selectedMonthApprove,
        approveNo: selectedNoApprove.toString(),
      }).toString();
      getInfoStepApprove(query);
    } else {
      getInfoStepApprove("");
    }
  };
  const fetchMonthOfSubmit = async () => {
    await getMonthCanApprove(selectedYear);
  };

  const removeSarchParam = () => {
    searchParams.delete("year");
    searchParams.delete("month");
    searchParams.delete("approveNo");
    searchParams.delete("companyId");
  };

  const resetSelections = () => {
    if (yearParam && monthParam && approveNoParam) {
      handleButtonMenuClick("approve");
      if (yearParam) setSelectedYearApprove(Number(yearParam));
      if (monthParam) setSelectedMonthApprove(monthParam);
      if (approveNoParam) setSelectedNoApprove(Number(approveNoParam));
      removeSarchParam();
    } else {
      setSelectedYearApprove(null);
      setSelectedMonthApprove(null);
      setSelectedNoApprove(null);
    }
  };

  const fetchOptionVocApprove = async () => {
    if (yearParam && !selectedYearApprove && !selectedMonthApprove) return;

    const queryParams = new URLSearchParams({ menu });
    if (selectedYearApprove) {
      queryParams.append("year", selectedYearApprove.toString());
    }
    if (selectedMonthApprove) {
      queryParams.append("month", selectedMonthApprove);
    }

    const queryString = queryParams.toString();

    const approves = await getOptionApprove(queryString);
    // console.log("ApproveOption HERE : ", approves);
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
    replaceFilter,
  } = usePagination<ResponseOthersReportVocs, FilterOthersReportVocsEmission>({
    action: searchReportVocEmission,
    remoteData: dataReportVoc.data,
    initialFilter: { year: [selectedYear], unit: selectedUnit, emission: selectedEmission },
  });

  // console.log("Source data :: ", dataReportVoc);

  const handleButtonMenuClick = (buttonName: string) => {
    // console.log("on click : ", buttonName, approveId);
    setSelectedButton(buttonName);
    if (buttonName === "table") {
      setOpenModalApproveStep(false);
      if (approveId) {
        searchParams.delete("approveId");
        searchParams.delete("companyId");
        setSearchParams(searchParams);
      }
    } else if (buttonName === "approve") {
      fetchOptionVocApprove();
      setOpenModalApproveStep(true);
    }
  };

  useEffect(() => {
    if (approveId) {
      handleButtonMenuClick("approve");
    }
  }, [approveId]);

  useEffect(() => {
    if (yearParam) setSelectedYear(yearParam);
    searchParams.delete("year");
  }, [yearParam]);

  const getMasterLabel = useCallback(
    (type: string, text: string) => {
      if (!text || typeof text != "string") return "";

      if (type === "area" && dataAreaInCompany) {
        const optValue = dataAreaInCompany.find((item) => item._id === text);
        return optValue?.areaName;
      } else return text;
    },
    [dataReportVoc, dataAreaInCompany],
  );
  const newEmissionOptions = useMemo(() => {
    if (!masterDataEmission) return [];

    const mapMasterEmission = masterDataEmission.map((item) => ({
      label: item.label,
      value: item.value,
    }));
    return [{ label: "All", value: "ALL" }, ...mapMasterEmission];
  }, [masterDataEmission]);
  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);
  const sourceNameFilter = useMemo(() => {
    if (filterReportVocEmission.sourceName?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterReportVocEmission.sourceName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterReportVocEmission.sourceName, dataReportVoc]);
  const areaFilter = useMemo(() => {
    return getFilterWithEmptyMasterdata(dataAreaInCompany, filterReportVocEmission.area, "areaName");
  }, [dataAreaInCompany, filterReportVocEmission.area]);

  const handleTableChange: TableProps<ResponseOthersReportVocs>["onChange"] = (_, filters) => {
    const mergeFilter = { ...filters, unit: selectedUnit };
    setQueryStringExcel(JSON.stringify(mergeFilter));
    onChange(mergeFilter);
  };
  const handleChangeUnit = () => {
    if (selectedUnit === UnitAllEnum.KG) {
      setSelectedUnit(UnitAllEnum.TON);
      const tmpQuery = queryStr ? { ...JSON.parse(queryStr), unit: UnitAllEnum.TON } : { unit: UnitAllEnum.TON };
      setQueryStringExcel(JSON.stringify(tmpQuery));
    } else if (selectedUnit === UnitAllEnum.TON) {
      const tmpQuery = queryStr ? { ...JSON.parse(queryStr), unit: UnitAllEnum.KG } : { unit: UnitAllEnum.KG };
      setQueryStringExcel(JSON.stringify(tmpQuery));
      setSelectedUnit(UnitAllEnum.KG);
    }
  };

  // console.log("dataReportVoc.data :: ", { reportVOC: dataReportVoc.data, filter });
  const addWidthWhenEmptyData = useCallback(
    (width: string, isForce?: string) => {
      if (dataReportVoc.data.length === 0) return { width };
      else if (isForce) return { width: isForce };
      else return {};
    },
    [dataReportVoc.data],
  );

  const getValueFromObjectMonth = (objectMonth: ReportMonthData) => {
    if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
    else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  };

  const monthColumns: TableProps<ResponseOthersReportVocs>["columns"] = MonthsDisplayNoTotal.map((item) => {
    return {
      title: item.title,
      key: item.dataIndex,
      render: (_, record) => {
        const showValue = getValueFromObjectMonth(record[item.dataIndex as keyof TmpReportEmissionData]);
        return <Text className="pr-3">{showValue ?? 0}</Text>;
      },
      align: "left",
    };
  });

  const getTotalValueFromObjectMonth = (objectMonth: ReportTotalMonthData) => {
    if (objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth.freeze[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
    else if (objectMonth[`value${selectedUnit}` as keyof ReportUnitValue] != null)
      return parseAndFormatScientificNumber(`${objectMonth[`value${selectedUnit}` as keyof ReportUnitValue]}`, 2);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseOthersReportVocs>["columns"] = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getIndex(+text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      align: "left",
    },
    {
      title: "Source Name",
      dataIndex: "sourceName",
      key: "sourceName",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{text}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: sourceNameFilter,
      align: "left",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getMasterLabel("area", text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: areaFilter,
      align: "left",
    },
    {
      title: "Emission",
      key: "emission",
      render: (_, record) => <Text>{convertEmissionWord(record.activeEmission)}</Text>,
      align: "left",
    },
    {
      title: (
        <>
          <Space>
            Unit
            <Button onClick={handleChangeUnit} type="text" icon={<TbRefresh />} />
          </Space>
        </>
      ),
      key: "unit",
      render: (_, __) => <Text>{selectedUnit}</Text>,
      align: "left",
      width: 80,
    },
    ...monthColumns,
    {
      title: "Total",
      render: (_, record) => <Text>{getTotalValueFromObjectMonth(record.total)}</Text>,
      align: "left",
      ...addWidthWhenEmptyData("4rem"),
    },
  ];

  // Note : get option in dorpdonw tvoc, bd, bz ...

  const initialRequestDidMount = async (isDidMount?: boolean) => {
    setLoadingAll(true);
    const getEmissionOptions = getMasterDataEmission();
    const getAreaInCompanyPromise = getAreaInCompany(profile?.activeCompany._id as string); // area
    const getFilterColumnReportVocEmissionPromise = getFilterColumnReportVocEmission(selectedYear);
    const searchReportVocEmissionPromise = searchReportVocEmission(
      isDidMount === true
        ? { ...filter, page, limit: pageSize, year: [selectedYear], unit: selectedUnit, emission: selectedEmission.toLowerCase() }
        : { page, limit: pageSize, year: [selectedYear], unit: selectedUnit, emission: selectedEmission.toLowerCase() },
    );

    await Promise.all([
      getEmissionOptions,
      getAreaInCompanyPromise,
      getFilterColumnReportVocEmissionPromise,
      searchReportVocEmissionPromise,
    ]);
    setLoadingAll(false);
  };

  useDidMount(() => {
    prevCompany.current = profile?.activeCompany._id;
    initialRequestDidMount(true);

    setQueryStringExcel(JSON.stringify({ year: [selectedYear], unit: selectedUnit, emission: selectedEmission }));
  });

  // ** NOTE : Trigger for Request new data -> when change company, year
  useEffect(() => {
    if (profile?.activeCompany) {
      if (prevCompany.current !== profile.activeCompany._id) {
        replaceFilter({ year: [currentYear.toString()], unit: selectedUnit, emission: selectedEmission });
        setSelectedYear(currentYear.toString());
        resetSelections();
        fetchOptionVocApprove();
        prevCompany.current = profile?.activeCompany._id;
      }
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear, selectedEmission]);

  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
      setQueryStringExcel(JSON.stringify({ year: [selectedYear], unit: selectedUnit, emission: selectedEmission }));
      fetchOptionVocApprove();
      fetchInfoStepApprove();
    }
  }, [tableKey]);

  useEffect(() => {
    if (optionApprove && "approveNo" in optionApprove) {
      const { approveNo } = optionApprove || {};
      if (approveNo) {
        if (!selectedNoApprove && selectedMonthApprove && approveNo?.length === 1) {
          setSelectedNoApprove(approveNo[0]);
        }
      } else {
        resetSelections();
      }
    }
  }, [optionApprove]);

  useEffect(() => {
    if (yearParam && approveNoParam) {
      handleButtonMenuClick("approve");
      if (yearParam) setSelectedYearApprove(Number(yearParam));
      if (monthParam) setSelectedMonthApprove(monthParam);
      if (approveNoParam) setSelectedNoApprove(Number(approveNoParam));
    }
  }, [yearParam, monthParam, approveNoParam]);

  useEffect(() => {
    fetchOptionVocApprove();
    fetchInfoStepApprove();
  }, [selectedYearApprove, selectedMonthApprove, selectedNoApprove]);

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    replaceFilter({ year: [year], unit: selectedUnit, emission: selectedEmission });
    setSelectedYearExcel(year);
    setQueryStringExcel(JSON.stringify({ year: [year], unit: selectedUnit, emission: selectedEmission }));
    setSelectedYear(year);
  };
  const buttonStyle = (buttonName: string) => ({
    backgroundColor: selectedButton === buttonName ? componentPalette.primary : "initial",
    color: selectedButton === buttonName ? "white" : "initial",
  });
  const handleOpenModalApproveMonth = () => {
    fetchMonthOfSubmit();
    setOpenModalApproveMonth(true);
  };

  const warningAlert = (message: string | string[], action: string) => {
    const warnModal = AntModal.confirm({
      title: (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Can not {action}</span>
          <CloseOutlined onClick={() => warnModal.destroy()} style={{ cursor: "pointer" }} />
        </div>
      ),
      content: message,
      okText: "OK",
      icon: <InfoCircleOutlined style={{ color: componentPalette.redThird }} />,
      okButtonProps: { style: { textAlign: "center", backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { display: "none" } },
      onOk: () => {},
      className: "custom-tmp-modal-warning",
    });
  };

  const handleSubmitReport = async (checkMonths: any) => {
    const userId = profile?.userId;
    const payload = {
      userId: userId as string,
      months: checkMonths,
      year: selectedYear,
    };
    createSubmitReportVoc(payload)
      .then(() => {
        messageApi.open({
          type: "success",
          content: CommonSuccess.SUBMIT_SUCCESSFULL,
        });
        setOpenModalApproveMonth(false);
      })
      .catch((error) => {
        const responseData = error?.response?.data as ResponseAPI;
        if (error?.response?.status === 400) {
          warningAlert(String(responseData.message), "submit");
        } else {
          messageApi.open({
            type: "error",
            content: CommonError.SUBMIT_FAIL,
          });
        }
        return;
      });
  };

  const handleApproveReport = async (approveBody: ApproveBody, action: string) => {
    updateApproveStep(approveBody)
      .then(() => {
        if (action === "approved") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.APPROVED_SUCCESSFULL,
          });
        } else if (action === "reject") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.REJECT_SUCCESSFULL,
          });
          // const findSlot = optionApprove.find((item) => item.value == approveBody.approveId);
          // const extractYear = findSlot?.label.split(" / ") as string[];
        }
        fetchInfoStepApprove();
        fetchMonthOfSubmit();
      })
      .catch((error) => {
        const actionStr = action === "approved" ? "approve" : "reject";
        warningAlert(error?.response?.data?.message || error?.message, actionStr);
        return;
      });
  };

  const isDisableSubmitButton = () => {
    // console.log("permissions : ", { dataLength: dataReportVoc.data?.length, optionMonthLength: optionMonth, permissions });
    if (!canApproveAndViewTable(permissions) || dataReportVoc.data?.length == 0) {
      return true;
    } else {
      return false;
    }
  };

  const handleChangeEmission = (value: string) => {
    setSelectedEmission(value);

    onChange({ emission: value });
    const tmpQuery = queryStr ? { ...JSON.parse(queryStr), emission: value } : { emission: value };
    setQueryStringExcel(JSON.stringify(tmpQuery));
  };

  // console.log("Emission selected : ", selectedEmission);

  const handleYearApproveChange = (value: number) => {
    setSelectedYearApprove(value);
    setSelectedMonthApprove(null);
    setSelectedNoApprove(null);
  };

  const handleMonthApprovalChange = (value: string) => {
    setSelectedMonthApprove(value);
    setSelectedNoApprove(null);
  };

  const handleApproveNoChange = (value: number) => {
    setSelectedNoApprove(value);
  };

  return (
    <Row className="w-full" id={"wrap-report-others-voc-table"}>
      {contextHolder}
      <Space direction={"horizontal"} align={"start"}>
        {selectedButton === "table" ? (
          <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        ) : (
          <Space>
            <Select
              value={selectedYearApprove}
              style={{ width: 120 }}
              onChange={handleYearApproveChange}
              options={optionApprove?.year?.map((year) => ({ label: year, value: year }))}
              loading={loading}
              size="middle"
            />
            <Select
              value={selectedMonthApprove}
              style={{ width: 120 }}
              onChange={handleMonthApprovalChange}
              options={optionApprove?.month?.map((month) => ({ label: capitalizeFirstLetter(month), value: month }))}
              loading={loading}
              size="middle"
            />
            <Select
              value={selectedNoApprove}
              style={{ width: 120 }}
              onChange={handleApproveNoChange}
              options={optionApprove?.approveNo?.map((approveNo) => ({ label: approveNo, value: approveNo }))}
              loading={loading}
              size="middle"
            />
          </Space>
        )}
        <div style={{}}>
          {selectedButton === "table" && (
            <Select
              value={selectedEmission}
              style={{ width: 120, visibility: selectedButton === "table" ? "visible" : "hidden" }}
              onChange={handleChangeEmission}
              options={newEmissionOptions}
              loading={loading}
              size="middle"
            />
          )}
        </div>
        <div>
          <Button
            size="middle"
            style={buttonStyle("table")}
            icon={<BsTable style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("table")}
          />
          <Button
            size="middle"
            style={buttonStyle("chart")}
            icon={<PiChartBarFill style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("chart")}
          />
          <Button
            size="middle"
            style={buttonStyle("approve")}
            icon={<HiOutlineClipboardDocumentCheck style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("approve")}
          />
        </div>
      </Space>
      {selectedButton === "table" && (
        <Table
          key={tableKey}
          columns={columns}
          style={{ whiteSpace: "nowrap" }}
          dataSource={canApproveAndViewMainReport(permissions) ? (sourceData as ResponseOthersReportVocs[]) : []}
          className="w-full mt-4"
          loading={loadingProfile || loadingAll || loading}
          rowKey={(record) => `${record._id}-${record.sourceName}-${uniqueId("rowKey")}`}
          pagination={false}
          onChange={handleTableChange}
          summary={(pageData) => {
            if (pageData.length > 0) {
              return (
                <Table.Summary fixed="bottom">
                  {dataReportVoc?.summary &&
                    dataReportVoc.summary.length > 0 &&
                    dataReportVoc.summary.map((row, index) => {
                      //   console.log("Here row :: ", row);
                      return (
                        <Table.Summary.Row key={`${index}-${uniqueId("rowKey")}-${Math.random()}`}>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <div style={{ textAlign: "center" }}>
                              <Text>{row.label}</Text>
                            </div>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} colSpan={1}>
                            <div style={{ textAlign: "start" }}>
                              <Text>{row.label === "Absolute" ? selectedUnit : row.unit}</Text>
                            </div>
                          </Table.Summary.Cell>
                          {MonthsDisplayNoTotal.map((month, idxMonth) => {
                            const keyMonth = month.dataIndex;
                            const rowValue = row[keyMonth as keyof typeof row];
                            const total =
                              typeof rowValue === "object" && rowValue !== null && UnitAllEnum.KG in rowValue && UnitAllEnum.TON in rowValue
                                ? rowValue[selectedUnit as keyof ObjectSummaryWithUnit]
                                : (rowValue as string | number);
                            return (
                              <>
                                <Table.Summary.Cell index={idxMonth + index} colSpan={1}>
                                  <div style={{ textAlign: "start" }}>
                                    <Text>
                                      {" "}
                                      {total
                                        ? row.key === "VocIntensity"
                                          ? parseAndFormatScientificNumber(`${total}`, 2)
                                          : parseAndFormatScientificNumber(`${total}`, 2)
                                        : 0}
                                    </Text>
                                  </div>
                                </Table.Summary.Cell>
                              </>
                            );
                          })}
                          <Table.Summary.Cell index={MonthsDisplayNoTotal.length} colSpan={1}>
                            <div style={{ textAlign: "start" }}>
                              <Text>
                                {row.key === "VocIntensity"
                                  ? parseAndFormatScientificNumber(`${row.total[selectedUnit as keyof ObjectSummaryWithUnit]}`, 2)
                                  : parseAndFormatScientificNumber(`${row.total[selectedUnit as keyof ObjectSummaryWithUnit]}`, 2)}
                              </Text>
                            </div>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      );
                    })}
                </Table.Summary>
              );
            }
          }}
        />
      )}

      {openModalApproveMonth && (
        <ModalApproveMonth
          open={openModalApproveMonth}
          intitailMonthly={optionMonth}
          loadingOption={loadingOptionMonth}
          loading={loadingSubmitVocReport}
          onSubmitReport={(checkedMonths) => {
            handleSubmitReport(checkedMonths);
          }}
          onCancelReport={() => {
            setOpenModalApproveMonth(false);
          }}
        />
      )}

      {selectedButton === "table" && (
        <Row className="w-full mt-4" justify={"end"}>
          {selectedButton == "table" && profile?.roles.includes(RolesNameEnum.COMPANY_ADMIN) && (
            <Button type="primary" style={{}} onClick={handleOpenModalApproveMonth} disabled={isDisableSubmitButton()}>
              Submit
            </Button>
          )}
        </Row>
      )}
      {openModalApproveStep && approveData && userId && selectedApprove && email && name && (
        <>
          {loadingOptionMonth || loadingGetApprove ? (
            <Row className="w-full" style={{ height: "4rem" }} justify={"center"}>
              <Spin style={{ display: "flex", justifyContent: "center", alignItems: "center" }} />
            </Row>
          ) : (
            <>
              <div style={{ height: "4rem" }} />
              <ModalApproveStep
                data={approveData}
                userId={userId}
                approveId={selectedApprove}
                email={email}
                name={name}
                onApprove={(approveBody, action) => {
                  handleApproveReport(approveBody, action);
                }}
              />
            </>
          )}
        </>
      )}
    </Row>
  );
};

export default ReportVocEmissionTable;
